import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { ExpenseCardService } from '../../../services/expenseCard.service';
import { AccountService } from '../../../services/account.service';
import { CorporateCardDTO } from '../../../interfaces/expense.interface';
import { Account } from '../../../interfaces/accounting.interface';
import EmployeeAutocomplete, { EmployeeOption } from '../../common/EmployeeAutocomplete';
import { AccountAutocomplete } from '../../common/AccountAutocomplete';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';

const cell = (basis: number, max: number) => ({ flex: `1 1 ${basis}px`, maxWidth: max, minWidth: 0 });

/** Corporate cards (W3-U6). Only the last 4 digits are ever entered or shown. */
const CorporateCards: React.FC = () => {
    const toast = useToast();
    const canPay = usePermission(PERMISSIONS.EXPENSE_CLAIM_PAY);
    const [cards, setCards] = useState<CorporateCardDTO[]>([]);
    const [liabilityAccounts, setLiabilityAccounts] = useState<Account[]>([]);
    const [holder, setHolder] = useState<EmployeeOption | null>(null);
    const [bank, setBank] = useState('');
    const [last4, setLast4] = useState('');
    const [accountId, setAccountId] = useState('');

    const reload = useCallback(async () => {
        try {
            setCards(await ExpenseCardService.listCards());
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load cards');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        reload();
        AccountService.getAccountsByType('LIABILITY').then(setLiabilityAccounts).catch(() => undefined);
    }, [reload]);

    const add = async () => {
        try {
            await ExpenseCardService.createCard({ holderUserId: holder!.id, bankName: bank.trim(), last4, liabilityAccountId: accountId });
            toast.showSuccess('Card registered');
            setHolder(null); setBank(''); setLast4('');
            await reload();
        } catch (e: any) {
            toast.showApiError(e, 'Could not register the card');
        }
    };

    const toggle = async (c: CorporateCardDTO) => {
        try {
            await ExpenseCardService.setCardActive(c.id, !c.isActive);
            await reload();
        } catch (e: any) {
            toast.showApiError(e, 'Could not update the card');
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }} data-help-page="expense.cards.page">
            <Typography variant="h5" sx={{ mb: 0.5 }}>Corporate cards</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Register each card with its holder and the liability account its statements settle. Only the last 4 digits are stored.
            </Typography>
            {canPay && (
                <Paper sx={{ p: 2, mb: 2 }} data-help-section="expense.cards.form">
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                        <EmployeeAutocomplete data-help="expense.cards.holder" value={holder} onChange={setHolder} label="Card holder" fullWidth={false} sx={cell(220, 320)} activeOnly />
                        <TextField data-help="expense.cards.bank" size="small" label="Bank" value={bank} onChange={e => setBank(e.target.value)} sx={cell(160, 240)} />
                        <TextField data-help="expense.cards.last4" size="small" label="Last 4 digits" value={last4} inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                                   onChange={e => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))} sx={cell(120, 160)} />
                        <AccountAutocomplete data-help="expense.cards.account" accounts={liabilityAccounts} value={accountId} onChange={setAccountId}
                                             label="Liability account" />
                        <Button data-help="expense.cards.add" variant="contained" disabled={!holder || !bank.trim() || last4.length !== 4 || !accountId} onClick={add}>
                            Add card
                        </Button>
                    </Box>
                </Paper>
            )}
            <TableContainer component={Paper} data-help-section="expense.cards.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="expense.cards.col.card">Card</TableCell><TableCell data-help="expense.cards.col.holder">Holder</TableCell><TableCell data-help="expense.cards.col.status">Status</TableCell>
                            {canPay && <TableCell data-help="expense.cards.col.actions" />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cards.length === 0 && <TableRow><TableCell colSpan={4}>No cards registered yet.</TableCell></TableRow>}
                        {cards.map(c => (
                            <TableRow key={c.id}>
                                <TableCell data-help="expense.cards.col.card">{c.label}</TableCell>
                                <TableCell data-help="expense.cards.col.holder">{c.holderName ?? '-'}</TableCell>
                                <TableCell data-help="expense.cards.col.status"><Chip size="small" label={c.isActive ? 'Active' : 'Inactive'} color={c.isActive ? 'success' : 'default'} /></TableCell>
                                {canPay && <TableCell align="right"><Button size="small" data-help="expense.cards.action.toggle" onClick={() => toggle(c)}>{c.isActive ? 'Deactivate' : 'Activate'}</Button></TableCell>}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default CorporateCards;
